import { ForwardedRef, forwardRef, useImperativeHandle, useRef } from 'react';
import classNames from 'classnames';

import { Country } from '@xemplo/country-helper';
import { MaybeErrorOrDescription } from '@xemplo/input-utils';

import { CountrySelector } from './country-selector/country-selector';
import { usePhoneFieldProvider } from './hooks/use-phone-field.provider';
import { ComponentContext } from './phone-field.context';
import * as S from './phone-field.styles';
import { PhoneFieldProps, PhoneFieldRef } from './phone-field.types';

export const PhoneField = forwardRef(
  (props: PhoneFieldProps, ref?: ForwardedRef<PhoneFieldRef>) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const context = usePhoneFieldProvider(props);
    const { metadata, state, inputProps, errorProps } = context;
    const { countryPrefixProps, fieldWrapperId, hasError, style, inputSize } = metadata;

    // We need a way to expose the ability to set the selected country
    // programmatically so we don't mess up with states mutating the
    // defaultCountryCode prop. This allows RHF integration to reset
    // the country when the form is reset.
    useImperativeHandle(ref, () => {
      const element = inputRef.current;
      if (!element) return null as unknown as PhoneFieldRef;

      if (!('setSelectedCountry' in element)) {
        Object.defineProperty(element, 'setSelectedCountry', {
          value: (countryCode: Country.Code) => {
            context.state.setSelectedCountry(countryCode);
          },
          writable: false,
          configurable: true,
        });
      }

      return element as PhoneFieldRef;
    }, [context.state]);

    return (
      <ComponentContext.Provider value={context}>
        <S.ComponentWrapper id={fieldWrapperId} style={style}>
          <S.FieldWrapper
            className={classNames(inputSize, 'no-padding-inline', { invalid: hasError })}
          >
            <CountrySelector parentId={fieldWrapperId} />
            <S.CustomXemploInput
              {...inputProps}
              ref={inputRef}
              leadingIcon={
                <S.CountryPrefix {...countryPrefixProps}>
                  {Country.getPhoneCodeByCountryCode(state.selectedCountry)}
                </S.CountryPrefix>
              }
            />
          </S.FieldWrapper>
          <MaybeErrorOrDescription {...errorProps} />
        </S.ComponentWrapper>
      </ComponentContext.Provider>
    );
  },
);

export default PhoneField;
